import { BaseAPICallsHandler } from "../BaseAPICallsHandler";
import { NetworkUtils } from "../NetworkUtils";
import { ServerConfig } from "../ServerConfig";
import { Order } from "../../../Models/Order";
import { Payment } from "../../../Models/Payment";
import { ErrorLogger } from "../../../utils/Logger";
import { SessionManager } from "../../SessionManager/SessionManager";
import { Buyer } from "../../../Models/Buyer";
import { Seller } from "../../../Models/Seller";
import { Delivery } from "../../../Models/Delivery";
import { SellerPlan } from "../../../Models/SellerPlan";

export interface DbMetricsResponse {
    timestamp: string;
    totalCollections: number;
    collections: {
        Buyers: number;
        Sellers: number;
        ActionCards: number;
        Deliveries: number;
        Gigs: number;
        Orders: number;
        Payments: number;
        ProgressReviews: number;
        Promotions: number;
        Reviews: number;
        Revisions: number;
        Categories: number;
        Subcategories: number;
    };
}

export class AdminAPIHandler {
    public static shared = new AdminAPIHandler();

    apiHandler = BaseAPICallsHandler.shared;
    serverConfig = ServerConfig.shared;
    networkUtils = NetworkUtils.shared;
    sessionManager = SessionManager.shared;

    constructor();
    constructor(
        apiHandler: BaseAPICallsHandler,
        serverConfig: ServerConfig,
        networkUtils: NetworkUtils
    );
    public constructor(...params: any[]) {
        if (params.length >= 3) {
            this.apiHandler = params[0];
            this.serverConfig = params[1];
            this.networkUtils = params[2];
        }
    }

    public async login(password: string): Promise<void> {
        let url = this.serverConfig.adminAPIs.adminLoginAPI;

        try {

            const response = await this.apiHandler.makePostRequest({}, url, { password });
            this.performPostLoginFlow(response)
            return Promise.resolve();

        } catch (err) {
            ErrorLogger.log(err, 'AdminAPIHandler.adminLogin');
            return Promise.reject(err);
        }
    }

    public async makePaymentEntryForOrderAPI(orderData: any, paymentData: any): Promise<{ order: Order; payment: Payment }> {
        const url = this.serverConfig.adminAPIs.makePaymentEntryForOrderAPI(orderData.id);
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {

            const response = await this.apiHandler.makePostRequest(authHeaders, url, { orderData, paymentData });
            const orderFromResp = response.data.order;
            const paymentObj = response.data.paymentObject;
            const order = new Order(orderFromResp);
            const payment = new Payment(paymentObj);
            return Promise.resolve({ order, payment });

        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async getOrderById(orderId: string): Promise<Order> {
        const url = this.serverConfig.adminAPIs.orderByIDAPI(orderId);
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makeGetRequest(authHeaders, url);
            const orderData = response.data.order;
            if (orderData) {
                const order = new Order(orderData);
                return Promise.resolve(order);
            } else {
                return Promise.reject("No Order with orderId - " + orderId + " found.")
            }
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async updateOrderById(orderData: any): Promise<Order> {
        const url = this.serverConfig.adminAPIs.orderByIDAPI(orderData.id);
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makePutRequest(authHeaders, url, orderData);
            const orderResp = response.data.order;
            const order = new Order(orderResp);
            return Promise.resolve(order);
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async getSellerPayableOrders(): Promise<Order[]> {
        const url = this.serverConfig.adminAPIs.sellerPayableOrdersAPI;
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makeGetRequest(authHeaders, url);
            const data = response.data.orders.map((orderJson: any) => new Order(orderJson));
            return Promise.resolve(data);
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async getOrdersByDateRange(startDate: string, endDate: string): Promise<Order[]> {
        const url = `${this.serverConfig.adminAPIs.ordersByDateRangeAPI}?startDate=${startDate}&endDate=${endDate}`;
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makeGetRequest(authHeaders, url);
            const data = response.data.orders.map((orderJson: any) => new Order(orderJson));
            return Promise.resolve(data);
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async getOrdersByBuyerId(buyerId: string): Promise<Order[]> {
        const url = this.serverConfig.adminAPIs.ordersByBuyerIdAPI(buyerId);
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makeGetRequest(authHeaders, url);
            const data = response.data.orders.map((orderJson: any) => new Order(orderJson));
            return Promise.resolve(data);
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async getOrdersBySellerId(sellerId: string): Promise<Order[]> {
        const url = this.serverConfig.adminAPIs.ordersBySellerIdAPI(sellerId);
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makeGetRequest(authHeaders, url);
            const data = response.data.orders.map((orderJson: any) => new Order(orderJson));
            return Promise.resolve(data);
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async getPaymentById(Id: string): Promise<Payment | undefined> {
        const url = this.serverConfig.adminAPIs.paymentByIDAPI(Id);
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makeGetRequest(authHeaders, url);
            const data = response.data.paymentObject;
            if (data) {
                const payment = new Payment(data);
                return Promise.resolve(payment);
            } else {
                return Promise.resolve(undefined);
            }
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async getDbMetrics(): Promise<DbMetricsResponse> {
        const url = this.serverConfig.adminAPIs.dbMetricsAPI;
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makeGetRequest(authHeaders, url);
            const data: DbMetricsResponse = response.data;
            return Promise.resolve(data);
        } catch (err) {
            return Promise.reject(err);
        }
    }

    // ========== Buyer Management ==========
    
    public async getBuyerById(buyerId: string): Promise<Buyer> {
        const url = this.serverConfig.adminAPIs.buyerByIdAPI(buyerId);
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makeGetRequest(authHeaders, url);
            const buyerData = response.data.buyer;
            if (buyerData) {
                const buyer = new Buyer(buyerData);
                return Promise.resolve(buyer);
            } else {
                return Promise.reject("No Buyer with buyerId - " + buyerId + " found.")
            }
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async getBuyerByEmail(email: string): Promise<Buyer> {
        const url = this.serverConfig.adminAPIs.buyerByEmailAPI(email);
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makeGetRequest(authHeaders, url);
            const buyerData = response.data.buyer;
            if (buyerData) {
                const buyer = new Buyer(buyerData);
                return Promise.resolve(buyer);
            } else {
                return Promise.reject("No Buyer with email - " + email + " found.")
            }
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async getBuyersByDateRange(startDate: string, endDate: string): Promise<Buyer[]> {
        const url = `${this.serverConfig.adminAPIs.buyersByDateRangeAPI}?startDate=${startDate}&endDate=${endDate}`;
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makeGetRequest(authHeaders, url);
            const data = response.data.buyers.map((buyerJson: any) => new Buyer(buyerJson));
            return Promise.resolve(data);
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async updateBuyerById(buyerId: string, buyerData: any): Promise<Buyer> {
        const url = this.serverConfig.adminAPIs.buyerByIdAPI(buyerId);
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makePutRequest(authHeaders, url, buyerData);
            const buyerResp = response.data.buyer;
            const buyer = new Buyer(buyerResp);
            return Promise.resolve(buyer);
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async updateBuyerByEmail(email: string, buyerData: any): Promise<Buyer> {
        const url = this.serverConfig.adminAPIs.buyerByEmailAPI(email);
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makePutRequest(authHeaders, url, buyerData);
            const buyerResp = response.data.buyer;
            const buyer = new Buyer(buyerResp);
            return Promise.resolve(buyer);
        } catch (err) {
            return Promise.reject(err);
        }
    }

    // ========== Seller Management ==========
    
    public async getSellerById(sellerId: string): Promise<Seller> {
        const url = this.serverConfig.adminAPIs.sellerByIdAPI(sellerId);
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makeGetRequest(authHeaders, url);
            const sellerData = response.data.seller;
            if (sellerData) {
                const seller = new Seller(sellerData);
                return Promise.resolve(seller);
            } else {
                return Promise.reject("No Seller with sellerId - " + sellerId + " found.")
            }
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async getSellerByEmail(email: string): Promise<Seller> {
        const url = this.serverConfig.adminAPIs.sellerByEmailAPI(email);
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makeGetRequest(authHeaders, url);
            const sellerData = response.data.seller;
            if (sellerData) {
                const seller = new Seller(sellerData);
                return Promise.resolve(seller);
            } else {
                return Promise.reject("No Seller with email - " + email + " found.")
            }
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async getSellersByDateRange(startDate: string, endDate: string): Promise<Seller[]> {
        const url = `${this.serverConfig.adminAPIs.sellersByDateRangeAPI}?startDate=${startDate}&endDate=${endDate}`;
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makeGetRequest(authHeaders, url);
            const data = response.data.sellers.map((sellerJson: any) => new Seller(sellerJson));
            return Promise.resolve(data);
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async updateSellerById(sellerId: string, sellerData: any): Promise<Seller> {
        const url = this.serverConfig.adminAPIs.sellerByIdAPI(sellerId);
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makePutRequest(authHeaders, url, sellerData);
            const sellerResp = response.data.seller;
            const seller = new Seller(sellerResp);
            return Promise.resolve(seller);
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async updateSellerByEmail(email: string, sellerData: any): Promise<Seller> {
        const url = this.serverConfig.adminAPIs.sellerByEmailAPI(email);
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makePutRequest(authHeaders, url, sellerData);
            const sellerResp = response.data.seller;
            const seller = new Seller(sellerResp);
            return Promise.resolve(seller);
        } catch (err) {
            return Promise.reject(err);
        }
    }

    // Delivery management methods
    public async getDeliveryById(deliveryId: string): Promise<Delivery> {
        const url = this.serverConfig.adminAPIs.deliveryByIdAPI(deliveryId);
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makeGetRequest(authHeaders, url);
            const deliveryResp = response.data.delivery;
            const delivery = new Delivery(deliveryResp);
            return Promise.resolve(delivery);
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async getDeliveryByOrderId(orderId: string): Promise<Delivery> {
        const url = this.serverConfig.adminAPIs.deliveryByOrderIdAPI(orderId);
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makeGetRequest(authHeaders, url);
            const deliveryResp = response.data.delivery;
            const delivery = new Delivery(deliveryResp);
            return Promise.resolve(delivery);
        } catch (err) {
            return Promise.reject(err);
        }
    }

    // ========== Seller Plan Management ==========

    public async createSellerPlan(planData: any): Promise<SellerPlan> {
        const url = this.serverConfig.adminAPIs.sellerPlanAPI;
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makePostRequest(authHeaders, url, planData);
            const planResp = response.data.plan;
            const plan = new SellerPlan(planResp);
            return Promise.resolve(plan);
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async getAllSellerPlans(activeOnly: boolean = false): Promise<SellerPlan[]> {
        const url = activeOnly 
            ? this.serverConfig.adminAPIs.activeSellerPlansAPI
            : `${this.serverConfig.adminAPIs.sellerPlanAPI}`;
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makeGetRequest(authHeaders, url);
            const data = response.data.plans.map((planJson: any) => new SellerPlan(planJson));
            return Promise.resolve(data);
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async getActiveSellerPlans(): Promise<SellerPlan[]> {
        const url = this.serverConfig.adminAPIs.activeSellerPlansAPI;
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makeGetRequest(authHeaders, url);
            const data = response.data.plans.map((planJson: any) => new SellerPlan(planJson));
            return Promise.resolve(data);
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async getSellerPlanById(planId: string): Promise<SellerPlan> {
        const url = this.serverConfig.adminAPIs.sellerPlanByIdAPI(planId);
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makeGetRequest(authHeaders, url);
            const planData = response.data.plan;
            if (planData) {
                const plan = new SellerPlan(planData);
                return Promise.resolve(plan);
            } else {
                return Promise.reject("No Seller Plan with planId - " + planId + " found.")
            }
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async updateSellerPlan(planId: string, planData: any): Promise<SellerPlan> {
        const url = this.serverConfig.adminAPIs.sellerPlanByIdAPI(planId);
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makePutRequest(authHeaders, url, planData);
            const planResp = response.data.plan;
            const plan = new SellerPlan(planResp);
            return Promise.resolve(plan);
        } catch (err) {
            return Promise.reject(err);
        }
    }

    public async deleteSellerPlan(planId: string): Promise<void> {
        const url = this.serverConfig.adminAPIs.sellerPlanByIdAPI(planId);
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            await this.apiHandler.makeDeleteRequest(authHeaders, url, {});
            return Promise.resolve();
        } catch (err) {
            return Promise.reject(err);
        }
    }

    // ========== Stream Chat Monitoring ==========

    public async getStreamChatAdminToken(): Promise<{ token: string; userId: string }> {
        const url = this.serverConfig.adminAPIs.streamChatAdminTokenAPI;
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makeGetRequest(authHeaders, url);
            const token = response.data?.token;
            const userId = response.data?.userId;
            if (token && userId) {
                return Promise.resolve({ token, userId });
            }
            return Promise.reject("Invalid stream chat admin token response.");
        } catch (err) {
            ErrorLogger.log(err, 'AdminAPIHandler.getStreamChatAdminToken');
            return Promise.reject(err);
        }
    }

    public async inspectStreamChatChannel(channelId: string): Promise<any> {
        const url = this.serverConfig.adminAPIs.streamChatChannelAPI(channelId);
        const authHeaders = this.networkUtils.addAdminAuthHeaders({});

        try {
            const response = await this.apiHandler.makeGetRequest(authHeaders, url);
            return Promise.resolve(response.data);
        } catch (err) {
            ErrorLogger.log(err, 'AdminAPIHandler.inspectStreamChatChannel');
            return Promise.reject(err);
        }
    }

    public performPostLoginFlow(response: any) {
        const jwtToken = response.data.jwt;
        this.sessionManager.adminLogin(jwtToken);
    }
}
